import { isRouteErrorResponse, useRouteError, Link } from 'react-router-dom';
import { logger } from '@services/logger/logger';
import { isApiError } from '@services/api/errors';

/**
 * Rendered by react-router when a route loader / action throws, or when a
 * child component throws during render of a route.
 *
 * In production we never surface raw error text to users. Devs see the full
 * detail via the logger sink (Sentry/Datadog/etc when wired in Phase 7).
 */
export function RouteErrorElement(): JSX.Element {
  const error = useRouteError();

  let heading = 'Something went wrong';
  let detail = 'The dashboard hit an unexpected error.';
  let status: number | undefined;

  if (isRouteErrorResponse(error)) {
    status = error.status;
    heading = error.status === 404 ? 'Route not found' : `Error ${String(error.status)}`;
    detail = typeof error.statusText === 'string' && error.statusText.length > 0
      ? error.statusText
      : detail;
  } else if (isApiError(error)) {
    status = error.status;
    heading = `Couldn’t load this page`;
    detail = 'The API responded with an error. Try again, or contact support if it persists.';
  }

  logger.error('route error', {
    kind: 'route',
    status,
    name: error instanceof Error ? error.name : undefined,
    message: error instanceof Error ? error.message : String(error),
  });

  return (
    <section role="alert" style={{ padding: 'var(--sp-7)', textAlign: 'center' }}>
      {status !== undefined ? (
        <p
          style={{
            fontFamily: 'var(--font-mono)',
            color: 'var(--pfl-magenta-bright)',
            fontSize: 'var(--fs-10)',
            letterSpacing: '0.18em',
          }}
        >
          {String(status)}
        </p>
      ) : null}
      <h1 style={{ marginTop: 'var(--sp-3)' }}>{heading}</h1>
      <p style={{ color: 'var(--pfl-text-mute)' }}>{detail}</p>
      <Link to="/dashboard">Return to dashboard</Link>
    </section>
  );
}
